import { useState, useCallback, useEffect, useRef } from 'react'

// LIVE_INTERVAL_MS is the lazy safety-net polling interval used while a live
// WebSocket connection is up (see docs/frontend/dynamic-refresh.md and
// specs/todos/2026-07-04-02-live-entity-notifications-websocket.md). Live
// updates handle the ~1-2s freshness; this interval only exists to recover
// from a missed/dropped hint the client never noticed, so 5 minutes is
// plenty lazy.
export const LIVE_INTERVAL_MS = 5 * 60 * 1000

export interface DynamicRefreshOptions {
  enabled?: boolean
  defaultInterval?: number // milliseconds
  minInterval?: number // milliseconds
  maxInterval?: number // milliseconds
  increaseRate?: number // multiplier for slower updates (e.g., 1.1 = 10% slower)
  decreaseRate?: number // multiplier for faster updates (e.g., 0.5 = 50% faster)
  /**
   * When true, pins refreshInterval to LIVE_INTERVAL_MS (5 min) instead of
   * running the adaptive 1s-30s backoff — the caller's live WebSocket
   * subscription (see contexts/LiveEventsContext.tsx's useScopeLive) is
   * expected to push updates in near-real-time, so aggressive polling on
   * top of that just wastes requests. Typically wired as
   * `live: useScopeLive(scope)`, so this flips back to false the instant
   * the socket drops and the hook seamlessly resumes adaptive polling from
   * wherever adjustRate last left refreshInterval — no reset, no jump to
   * the fastest interval, since a dropped socket doesn't mean "data is
   * suddenly changing fast," it means "fall back to the same safety net
   * that was always running underneath."
   */
  live?: boolean
}

export interface DynamicRefreshState {
  // The EFFECTIVE current refresh interval in milliseconds — pinned to
  // LIVE_INTERVAL_MS while `live` is true, otherwise the adaptive value
  // adjustRate has converged on. This is "how often will this actually
  // refetch right now," not the paused-while-live adaptive internal state.
  refreshInterval: number
  timeRemaining: number // Time remaining until next refresh in seconds
  isEnabled: boolean
  hasChanged: boolean | null // null = initial state, true/false = change detected
  lastTimestamp: string | null
}

export interface DynamicRefreshControls {
  enable: () => void
  disable: () => void
  reset: () => void
  forceRefresh: () => void
  adjustRate: (hasChanged: boolean, newTimestamp?: string) => void
}

export interface DynamicRefreshReturn extends DynamicRefreshState {
  controls: DynamicRefreshControls
}

/**
 * Dynamic refresh hook that adjusts refresh rates based on data change frequency.
 *
 * Behavior:
 * - Increases refresh interval (slower updates) when no changes are detected
 * - Decreases refresh interval (faster updates) when changes are detected
 * - Monitors timestamps to detect changes between refreshes
 * - Provides countdown timer and control functions
 *
 * Usage:
 * ```typescript
 * const dynamicRefresh = useDynamicRefresh({
 *   enabled: true,
 *   defaultInterval: 1000, // 1 second
 * })
 *
 * // In your data fetching effect:
 * useEffect(() => {
 *   if (dynamicRefresh.timeRemaining <= 0) {
 *     // Fetch data and extract latest timestamp
 *     const latestTimestamp = extractTimestamp(data)
 *     dynamicRefresh.controls.adjustRate(
 *       latestTimestamp !== dynamicRefresh.lastTimestamp
 *     )
 *   }
 * }, [dynamicRefresh.timeRemaining])
 * ```
 */
export const useDynamicRefresh = (options: DynamicRefreshOptions = {}): DynamicRefreshReturn => {
  const {
    enabled = true,
    defaultInterval = 1000, // 1 second
    minInterval = 1000, // 1 second minimum
    maxInterval = 30000, // 30 seconds maximum
    increaseRate = 1.1, // 10% slower when no changes
    decreaseRate = 0.5, // 50% faster when changes detected
    live = false,
  } = options

  // State — this is the ADAPTIVE interval, tracked continuously regardless
  // of `live`. While live is true, the countdown timer below runs against
  // LIVE_INTERVAL_MS instead of this value (see the `effectiveInterval`
  // computation), but adjustRate is still free to update this underlying
  // state if the caller keeps calling it — so the moment `live` flips back
  // to false, polling resumes from wherever the adaptive value already was,
  // not from defaultInterval. This is the "seamless fallback" the spec
  // calls for: a dropped socket isn't a signal that data is suddenly
  // changing fast, so there is no reason to reset to the fastest interval.
  const [refreshInterval, setRefreshInterval] = useState(defaultInterval)
  const [timeRemaining, setTimeRemaining] = useState(Math.round(defaultInterval / 1000))
  const [isEnabled, setIsEnabled] = useState(enabled)
  const [hasChanged, setHasChanged] = useState<boolean | null>(null)
  const [lastTimestamp, setLastTimestamp] = useState<string | null>(null)

  // effectiveInterval is what the countdown timer actually runs against —
  // pinned to the lazy live safety net while `live` is true, otherwise the
  // normal adaptive value.
  const effectiveInterval = live ? LIVE_INTERVAL_MS : refreshInterval

  // Refs for cleanup
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Adjust refresh rate based on whether data has changed
  const adjustRate = useCallback((dataHasChanged: boolean, newTimestamp?: string) => {
    setHasChanged(dataHasChanged)

    // Update timestamp if provided
    if (newTimestamp !== undefined) {
      setLastTimestamp(newTimestamp)
    }

    setRefreshInterval(prev => {
      let newInterval: number

      if (dataHasChanged) {
        // Decrease interval (faster updates) when changes detected
        newInterval = Math.max(minInterval, prev * decreaseRate)
      } else {
        // Increase interval (slower updates) when no changes
        newInterval = Math.min(maxInterval, prev * increaseRate)
      }

      // Update time remaining for the new interval
      setTimeRemaining(Math.round(newInterval / 1000))

      return newInterval
    })
  }, [minInterval, maxInterval, increaseRate, decreaseRate])

  // Reset to default interval
  const reset = useCallback(() => {
    setRefreshInterval(defaultInterval)
    setTimeRemaining(Math.round(defaultInterval / 1000))
    setHasChanged(null)
    setLastTimestamp(null)
  }, [defaultInterval])

  // Enable/disable controls
  const enable = useCallback(() => {
    setIsEnabled(true)
  }, [])

  const disable = useCallback(() => {
    setIsEnabled(false)
    // Clear any running countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
  }, [])

  // Force immediate refresh (resets countdown)
  const forceRefresh = useCallback(() => {
    setTimeRemaining(Math.round(effectiveInterval / 1000))
  }, [effectiveInterval])

  // Countdown timer management. Runs against effectiveInterval (pinned to
  // LIVE_INTERVAL_MS while live, otherwise the adaptive refreshInterval) so
  // a live->non-live transition seamlessly retargets the SAME running
  // timer at the adaptive value instead of restarting it — the countdown
  // that was already ticking just starts counting down from a different
  // total, per forceRefresh-style semantics on the next tick boundary.
  useEffect(() => {
    if (!isEnabled) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
      return
    }

    // Clear any existing interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
    }

    // Start countdown timer
    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          // Reset countdown to the current effective interval after reaching 0
          return Math.round(effectiveInterval / 1000)
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [isEnabled, effectiveInterval])

  // Update enabled state when options change
  useEffect(() => {
    setIsEnabled(enabled)
  }, [enabled])

  // Re-sync the displayed countdown whenever `live` itself flips, so
  // switching modes doesn't leave timeRemaining showing a stale countdown
  // relative to the interval it now belongs to (e.g. "3s" lingering right
  // after jumping to the 5-minute live interval). Deliberately keyed on
  // `live` alone, not effectiveInterval — adjustRate changing the adaptive
  // value while NOT live should keep its own already-correct countdown
  // behavior (handled by the timer effect above), not get force-reset here.
  useEffect(() => {
    setTimeRemaining(Math.round(effectiveInterval / 1000))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
      }
    }
  }, [])

  return {
    refreshInterval: effectiveInterval,
    timeRemaining,
    isEnabled,
    hasChanged,
    lastTimestamp,
    controls: {
      enable,
      disable,
      reset,
      forceRefresh,
      adjustRate,
    },
  }
}

/**
 * Utility function to extract the most recent timestamp from job and execution data
 */
export const extractMostRecentTimestamp = (
  job: { updated_at: string } | null | undefined,
  executions: Array<{ updated_at: string }> = []
): string | null => {
  if (!job) return null

  const timestamps = [job.updated_at]

  // Add execution timestamps if available
  executions.forEach(exec => {
    if (exec.updated_at) {
      timestamps.push(exec.updated_at)
    }
  })

  // Find the most recent timestamp
  const mostRecentTimestamp = timestamps.reduce((latest, current) => {
    const currentTime = new Date(current).getTime()
    const latestTime = new Date(latest).getTime()
    return currentTime > latestTime ? current : latest
  })

  return mostRecentTimestamp
}